import plur from 'plur';
import { z } from '@customerio/hydra-validation';
import type { CustomersDeleteParams } from '@customerio/hydra-ui-api';
import { selectionExceedsLoaded, type PeopleSelectionSummary } from '@/features/people';
import type { Customer } from '@/services/db/customer/entity';
import { cioId, hasConfirmedDoubleOptIn } from '@/services/db/customer/utils';
import { formatNumber } from '@/utils/number';
import { isRecord } from '@/utils/objects';
import { decodeFilters } from './list-params';

/**
 * Gating and payload logic for the People-list bulk actions. Each predicate is
 * one selection-state branch: most actions operate per-loaded-row, so they
 * disable when the selection covers more profiles than the page holds
 * (`selectionExceedsLoaded`); delete instead switches to its filter-based
 * payload in that state.
 */

/** Unsubscribe acts on loaded rows only. */
export function canUnsubscribe(selection: PeopleSelectionSummary): boolean {
  return !selectionExceedsLoaded(selection) && selection.selectedCount > 0;
}

/** Add-to-manual-segment is a Builder-plan action and adds by profile id. */
export function canAddToManualSegment(
  selection: PeopleSelectionSummary,
  { isBuilder }: { isBuilder: boolean },
): boolean {
  if (!isBuilder) return false;
  if (selectionExceedsLoaded(selection)) return false;
  return selection.selectedCount > 0;
}

/** Inviting test users is a Builder-plan action requiring edit permission. */
export function canInviteTestUsers(
  selection: PeopleSelectionSummary,
  { isBuilder, canEdit }: { isBuilder: boolean; canEdit: boolean },
): boolean {
  if (!isBuilder || !canEdit) return false;
  if (selectionExceedsLoaded(selection)) return false;
  return selection.selectedCount > 0;
}

/**
 * Setting per-contact email tracking consent updates each loaded record, so
 * the filter-based select-all path (no loaded records to update) is out.
 */
export function canSetEmailTrackingConsent(
  selection: PeopleSelectionSummary,
  { consentFlagEnabled, canEdit }: { consentFlagEnabled: boolean; canEdit: boolean },
): boolean {
  if (!consentFlagEnabled || !canEdit) return false;
  if (selectionExceedsLoaded(selection)) return false;
  return selection.selectedCount > 0;
}

/** The select-all-across-pages state, where delete goes by filter instead of ids. */
export function isBulkDeletion(selection: PeopleSelectionSummary): boolean {
  return selectionExceedsLoaded(selection);
}

/**
 * Delete allows the filter-based select-all path — but only when a search is
 * active (otherwise one page holds everything and explicit ids are used), and
 * only for the structured filter wire (see `buildBulkDeleteMatch`).
 */
export function canDelete(
  selection: PeopleSelectionSummary,
  { noFiltersApplied, filters }: { noFiltersApplied: boolean; filters: string | null },
): boolean {
  if (isBulkDeletion(selection)) {
    return !noFiltersApplied && !isLegacyArrayFilterQuery(filters);
  }
  return selection.selectedCount > 0;
}

/** Merge takes exactly 2 profiles and edit permission. */
export function isMergeDisabled(
  { allSelected, totalCount, selectedCount }: PeopleSelectionSummary,
  canEdit: boolean,
): boolean {
  return !canEdit || selectedCount > 2 || (allSelected && totalCount > 2) || totalCount < 2;
}

export function inviteTestUsersLabel(selectedCount: number): string {
  return selectedCount === 1 ? 'Invite test user' : 'Invite test users';
}

/** "N selected people" — the count is the whole result set when all are selected. */
export function selectedPeopleLabel(
  { allSelected, totalCount, selectedCount }: PeopleSelectionSummary,
  { personSingular, personPlural }: { personSingular: string; personPlural: string },
): string {
  const count = allSelected ? totalCount : selectedCount;
  return `${formatNumber(count)} selected ${plur(personSingular, personPlural, count)}`;
}

/* ------------------------------------------------------------------------- */
/* Delete payload                                                             */
/* ------------------------------------------------------------------------- */

type DeleteFilterWire = NonNullable<CustomersDeleteParams['filters']>;

/** The match half of the bulk-delete body (`suppress` is merged in by the caller). */
export type BulkDeleteMatch = Pick<CustomersDeleteParams, 'type' | 'internal_ids' | 'email' | 'filters'>;

/**
 * The structured filter wire (`{ and: [...] }` / `{ or: [...] }` / a single
 * leaf) is already the shape the delete endpoint takes, so it passes through
 * unchanged — every `Filter` field is optional, and the value comes from the
 * app's own encoder. The legacy bare-array wire from old deep links is NOT
 * convertible here — the serializer lives with the retired condition editor
 * (dryad-first decision) — so it never reaches a delete payload; `canDelete`
 * refuses it.
 */
const StructuredFilterWire = z.custom<DeleteFilterWire>(isRecord);

/** True when the `filters` param decodes to the legacy bare-array wire. */
export function isLegacyArrayFilterQuery(filters: string | null): boolean {
  if (filters == null || filters === '') return false;
  const decoded = decodeFilters(filters);
  return Array.isArray(decoded) && decoded.length > 0;
}

/**
 * The delete request's match strategy: explicit ids for a per-row selection
 * (or a select-all that fits on one page), the condition filter or email
 * search for a cross-page select-all. `null` when no valid match exists —
 * callers never send a delete in that state.
 */
export function buildBulkDeleteMatch({
  selection,
  noFiltersApplied,
  selectedCioIds,
  filters,
  email,
}: {
  selection: PeopleSelectionSummary;
  noFiltersApplied: boolean;
  selectedCioIds: readonly string[];
  filters: string | null;
  email: string;
}): BulkDeleteMatch | null {
  if (!isBulkDeletion(selection) || noFiltersApplied) {
    return { type: 'internal_ids', internal_ids: [...selectedCioIds] };
  }
  if (filters != null && filters !== '') {
    const decoded = StructuredFilterWire.safeParse(decodeFilters(filters));
    return decoded.success ? { type: 'filters', filters: decoded.data } : null;
  }
  if (email !== '') {
    return { type: 'email', email };
  }
  return null;
}

/* ------------------------------------------------------------------------- */
/* Invite test users                                                          */
/* ------------------------------------------------------------------------- */

export interface InviteTestUsersPartition {
  /** Profiles eligible for the double-opt-in invite, by `cio_id`. */
  cioIds: string[];
  alreadyVerified: number;
  skippedNoEmail: number;
  /** Profiles that can't be invited at all (no `cio_id`) — counted as failures. */
  preFailed: number;
}

/** Split the selection into invitable profiles and the skip/failure tallies. */
export function partitionInviteTestUsers(
  people: ReadonlyArray<Pick<Customer, 'attributes' | 'identifiers'>>,
): InviteTestUsersPartition {
  const partition: InviteTestUsersPartition = { cioIds: [], alreadyVerified: 0, skippedNoEmail: 0, preFailed: 0 };
  for (const person of people) {
    const email = person.attributes.email;
    if (email == null || email === '') {
      partition.skippedNoEmail += 1;
      continue;
    }
    if (hasConfirmedDoubleOptIn(person.attributes)) {
      partition.alreadyVerified += 1;
      continue;
    }
    const id = cioId(person);
    if (id == null) {
      partition.preFailed += 1;
      continue;
    }
    partition.cioIds.push(id);
  }
  return partition;
}

/**
 * The invite outcome snackbar: each non-zero tally becomes a sentence, joined
 * with periods; an empty selection outcome gets the standalone message.
 */
export function inviteOutcomeMessage({
  invited,
  alreadyVerified,
  skippedNoEmail,
  failed,
  personSingular,
  personPlural,
}: {
  invited: number;
  alreadyVerified: number;
  skippedNoEmail: number;
  failed: number;
  personSingular: string;
  personPlural: string;
}): string {
  const parts: string[] = [];
  if (invited > 0) {
    const noun = invited === 1 ? personSingular : personPlural;
    parts.push(`Sent confirmation to add ${formatNumber(invited)} ${noun} as test ${plur('user', invited)}`);
  }
  if (alreadyVerified > 0) parts.push(`${formatNumber(alreadyVerified)} already verified`);
  if (skippedNoEmail > 0) parts.push(`${formatNumber(skippedNoEmail)} skipped (no email)`);
  if (failed > 0) parts.push(`${formatNumber(failed)} failed`);
  if (parts.length === 0) {
    return `No ${personPlural} to invite as test users`;
  }
  return `${parts.join('. ')}.`;
}

/* ------------------------------------------------------------------------- */
/* Merge                                                                      */
/* ------------------------------------------------------------------------- */

export interface MergeLinkParams {
  source: 'people_list';
  email?: string;
  filters?: string;
  primary_id?: string;
  secondary_id?: string;
}

/**
 * Query params for the merge page: the current search carries over, and with
 * two profiles selected the older one (by `created_at`) becomes the primary.
 * The snake_case pair is the URL spelling the embedded merge route reads —
 * its controller aliases them onto its own camelCase properties.
 */
export function buildMergeLinkParams({
  selectedPeople,
  email,
  filters,
}: {
  selectedPeople: ReadonlyArray<Pick<Customer, 'attributes' | 'identifiers'>>;
  email: string;
  filters: string | null;
}): MergeLinkParams {
  const params: MergeLinkParams = { source: 'people_list' };
  if (email !== '') params.email = email;
  if (filters != null && filters !== '') params.filters = filters;

  if (selectedPeople.length === 1) {
    params.primary_id = cioId(selectedPeople[0]);
  } else if (selectedPeople.length === 2) {
    const oldestFirst = [...selectedPeople].sort(
      (a, b) => Number(a.attributes.created_at) - Number(b.attributes.created_at),
    );
    params.primary_id = cioId(oldestFirst[0]);
    params.secondary_id = cioId(oldestFirst[1]);
  }
  return params;
}
